export function isImageDocumentType(filename: string): boolean {
  return ['jpg', 'jpeg', 'png', 'gif', 'webp'].some((ext) => filename.toLowerCase().endsWith(ext));
}

export function isIFCDocumentType(filename: string): boolean {
  return filename.toLowerCase().endsWith('.ifc');
}

export function isPDFDocumentType(filename: string): boolean {
  return [
    'pdf',
    'docx',
    'doc',
    'xls',
    'xlsx',
    'xlsm',
    '.ppt',
    '.pptx',
    'xml',
    'odp',
    'odt',
    'ott',
    'ods',
    'csv',
    'tsv',
    'txt',
    'html',
    'rtf',
  ].some((ext) => filename.toLowerCase().endsWith(ext));
}

export function isHTMLDocumentType(filename: string): boolean {
  return filename.toLowerCase().endsWith('.html');
}

export function isJSONDocumentType(filename: string): boolean {
  return filename.toLowerCase().endsWith('.json');
}

export function isMarkdownDocumentType(filename: string): boolean {
  return filename.toLowerCase().endsWith('.md') || filename.toLowerCase().endsWith('.markdown');
}

export function isKMLDocumentType(filename: string): boolean {
  return filename.toLowerCase().endsWith('.kml');
}

export function isMsWordType(filename: string): boolean {
  return ['doc', 'docx', 'xls', 'xlsx', 'xlsm'].some((ext) => filename.toLowerCase().endsWith(ext));
}

export function isExcelDocumentType(filename: string): boolean {
  return ['xls', 'xlsx', 'xlsm'].some((ext) => filename.toLowerCase().endsWith(ext));
}

export function isValidDocumentType(filename: string): boolean {
  return (
    isImageDocumentType(filename) ||
    isIFCDocumentType(filename) ||
    isPDFDocumentType(filename) ||
    isHTMLDocumentType(filename) ||
    isJSONDocumentType(filename) ||
    isMarkdownDocumentType(filename)
  );
}

export const mapExtensionToUppercase = (extension: string) => {
  if (extension) {
    return extension.replace('.', '').toUpperCase();
  } else return 'UNKNOWN';
};

/**
 * Minimum shape a document/blob must satisfy for the viewer source-resolution
 * helpers below. Both `Document` and `Blob` satisfy this.
 */
interface ViewableRecord {
  name: string;
  fileUrl: string | null;
  downloadUrl: string | null;
  convertedPdfFileId?: string | null;
}

/**
 * Pick the right source URL for the DocumentViewer. Excel needs the raw
 * download URL; documents the backend already converted to PDF use `fileUrl`
 * (which points at the converted PDF).
 */
export function resolveDocumentSrc(record: ViewableRecord): string {
  if (isExcelDocumentType(record.name)) {
    return record.downloadUrl || record.fileUrl || '';
  }
  return record.fileUrl || record.downloadUrl || '';
}

/**
 * Force the DocumentViewer to the right renderer when auto-detection from the
 * file extension would pick the wrong one. Converted Office documents
 * (e.g. `report.docx` whose `fileUrl` serves the backend-generated PDF) need
 * an explicit `'pdf'`; Excel needs `'excel'`. Everything else auto-detects
 * from the file name.
 */
export function resolveDocumentFileType(record: ViewableRecord): string | undefined {
  if (isExcelDocumentType(record.name)) return 'excel';
  if (record.convertedPdfFileId && !record.name.toLowerCase().endsWith('.pdf')) return 'pdf';
  return undefined;
}
